import { z } from "zod";
import { Capability, Freshness, ServingFieldType, UsageRights } from "./exposure-contract";

/** Public, signed, compact description of a data product. Powers global discovery. */
export const DatasetManifest = z.object({
  manifestVersion: z.literal(1),
  datasetId: z.string(),
  providerId: z.string(),
  providerName: z.string(),
  name: z.string().min(3).max(120),
  description: z.string().max(2000),
  categories: z.array(z.string().max(48)).max(10),
  keywords: z.array(z.string().max(48)).max(40).default([]),
  entities: z.array(z.object({
    name: z.string(),
    description: z.string().max(500).optional(),
    fields: z.array(z.object({ name: z.string(), type: ServingFieldType, description: z.string().max(300).optional() })),
  })),
  capabilities: z.array(Capability),
  coverage: z.object({
    geographic: z.array(z.string().max(64)).max(50).default([]), // ISO-3166 + city, e.g. "AU", "AU-NSW", "AU/Sydney"
    historicalFrom: z.string().nullable().default(null),
    rowCountApprox: z.number().int().nonnegative().default(0),
  }),
  freshness: z.object({ target: Freshness, targetSeconds: z.number().int().nullable(), lastUpdatedAt: z.string().nullable(), avgActualLagSeconds: z.number().nullable() }),
  scores: z.object({
    quality: z.number().min(0).max(1),
    completeness: z.number().min(0).max(1),
    reliability: z.number().min(0).max(1),
    freshness: z.number().min(0).max(1),
    exclusivity: z.number().min(0).max(1),
  }),
  estimatedLatencyMs: z.number().int().nonnegative(),
  pricing: z.object({ currency: z.literal("USD"), pricePerQueryMicros: z.number().int().nonnegative(), tiers: z.array(z.object({ fromQueriesPerMonth: z.number().int(), pricePerQueryMicros: z.number().int() })).default([]) }),
  rights: UsageRights,
  cache: z.object({ scope: z.enum(["PUBLIC", "DATASET", "CONSUMER", "NO_CACHE"]), maxTtlSeconds: z.number().int() }),
  privacyClass: z.enum(["public_aggregate", "business_data", "sanitised_records"]),
  jurisdiction: z.string(),
  nodeLocations: z.array(z.string()).default([]),
  apiVersion: z.literal("v1"),
  schemaVersion: z.number().int().min(1),
  contractVersion: z.number().int().min(1),
  publishedAt: z.string(),
  freeSample: z.boolean().default(false),
});
export type DatasetManifest = z.infer<typeof DatasetManifest>;
